import { useEffect, useRef, useState } from 'react';
import {
  Button, Caption1, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle,
  MessageBar, MessageBarBody, Spinner, Text, makeStyles,
} from '@fluentui/react-components';
import type { PersonFileListItem } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { loadPdfJs, renderPdfPage, PDF_DOCUMENT_OPTIONS } from '../../lib/pdf.js';
import { fetchFileContent, type FileContent, type FileProblem } from './api.js';
import { downloadFile, problemCopy, problemOf } from './download.js';
import { viewerKindFor } from './model.js';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  nav: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
  stage: { overflow: 'auto', maxHeight: '70vh', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium },
  canvas: { display: 'block', marginInline: 'auto', maxWidth: '100%', height: 'auto' },
  image: { display: 'block', marginInline: 'auto', maxWidth: '100%', height: 'auto' },
});

export const VIEW_RECORDED_COPY = 'Opening or downloading a file is recorded in this person’s audit history.';

export interface FileViewerProps {
  readonly personId: string;
  /** The file to show; `null` closes the viewer. */
  readonly item: PersonFileListItem | null;
  readonly onClose: () => void;
}

/**
 * The in-app viewer (ADR-009 §4). A dialog, never a route: opening a file changes nothing in
 * the address bar or history. Bytes live only while it is open.
 */
export function FileViewer({ personId, item, onClose }: FileViewerProps) {
  return (
    <Dialog open={item !== null} onOpenChange={(_, data) => (data.open ? null : onClose())}>
      <DialogSurface>
        {item === null ? null : <ViewerBody key={item.fileId} personId={personId} item={item} onClose={onClose} />}
      </DialogSurface>
    </Dialog>
  );
}

type Load = { readonly status: 'loading' } | { readonly status: 'ready'; readonly content: FileContent } | { readonly status: 'error'; readonly problem: FileProblem };

function ViewerBody({ personId, item, onClose }: { readonly personId: string; readonly item: PersonFileListItem; readonly onClose: () => void }) {
  const styles = useStyles();
  const kind = viewerKindFor(item.contentType);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [downloading, setDownloading] = useState(false);
  const [downloadProblem, setDownloadProblem] = useState<FileProblem | null>(null);

  useEffect(() => {
    // A download-only type is not fetched inline at all: that would record a view nobody saw.
    if (kind === 'download') return;
    const controller = new AbortController();
    fetchFileContent(personId, item.fileId, 'inline', controller.signal).then(
      (content) => {
        // The server's type must agree with the list's; a surprise is not rendered.
        if (viewerKindFor(content.contentType) !== kind) setLoad({ status: 'error', problem: 'failed' });
        else setLoad({ status: 'ready', content });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) setLoad({ status: 'error', problem: problemOf(error) });
      },
    );
    return () => controller.abort();
  }, [personId, item.fileId, kind]);

  async function download() {
    setDownloading(true);
    setDownloadProblem(null);
    try {
      await downloadFile(personId, item);
    } catch (error) {
      setDownloadProblem(problemOf(error));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <DialogBody>
      <DialogTitle>{item.displayName}</DialogTitle>
      <DialogContent className={styles.body}>
        <Caption1>{VIEW_RECORDED_COPY}</Caption1>
        {kind === 'download' ? (
          <Text>This type of file cannot be shown in the app. Download it to open it.</Text>
        ) : load.status === 'loading' ? (
          <Spinner size="small" label="Opening the file" />
        ) : load.status === 'error' ? (
          <MessageBar intent={load.problem === 'unavailable' ? 'warning' : 'error'}>
            <MessageBarBody>{problemCopy[load.problem]}</MessageBarBody>
          </MessageBar>
        ) : kind === 'pdf' ? (
          <PdfView bytes={load.content.bytes} label={item.displayName} />
        ) : (
          <ImageView bytes={load.content.bytes} contentType={load.content.contentType} label={item.displayName} />
        )}
        {downloadProblem === null ? null : (
          <MessageBar intent="error"><MessageBarBody>{problemCopy[downloadProblem]}</MessageBarBody></MessageBar>
        )}
      </DialogContent>
      <DialogActions>
        <Button appearance={kind === 'download' ? 'primary' : 'secondary'} disabled={downloading} onClick={() => void download()}>
          {downloading ? 'Downloading…' : 'Download'}
        </Button>
        <Button appearance="secondary" onClick={onClose}>Close</Button>
      </DialogActions>
    </DialogBody>
  );
}

/** An object URL for the image, revoked when the viewer closes or moves to another file. */
function ImageView({ bytes, contentType, label }: { readonly bytes: ArrayBuffer; readonly contentType: string; readonly label: string }) {
  const styles = useStyles();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: contentType }));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [bytes, contentType]);
  return <div className={styles.stage}>{url === null ? null : <img className={styles.image} src={url} alt={label} />}</div>;
}

/**
 * pdf.js onto a canvas, one page at a time (the same budget the portal's pdf-form keeps for
 * iOS canvas memory). No iframe, no `<embed>`, no browser PDF plugin.
 */
function PdfView({ bytes, label }: { readonly bytes: ArrayBuffer; readonly label: string }) {
  const styles = useStyles();
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loading: PDFDocumentLoadingTask | undefined;
    void (async () => {
      try {
        const pdfjs = await loadPdfJs();
        if (cancelled) return;
        // pdf.js transfers the buffer to its worker; hand it a copy.
        loading = pdfjs.getDocument({ data: new Uint8Array(bytes.slice(0)), ...PDF_DOCUMENT_OPTIONS });
        const loaded = await loading.promise;
        if (!cancelled) setDoc(loaded);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      // Destroying the task tears down the document and its worker with it.
      void loading?.destroy().catch(() => {});
    };
  }, [bytes]);

  useEffect(() => {
    if (doc === null) return;
    let cancelled = false;
    let task: { cancel: () => void } | undefined;
    void (async () => {
      setRendering(true);
      try {
        const page = await doc.getPage(pageIndex + 1);
        const target = canvas.current;
        if (cancelled || target === null) return;
        const drawn = renderPdfPage(page, target);
        if (drawn === null) throw new Error('No canvas context');
        task = drawn.task;
        await drawn.task.promise;
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, pageIndex]);

  if (failed) {
    return <MessageBar intent="error"><MessageBarBody>This PDF could not be displayed. Download it to open it.</MessageBarBody></MessageBar>;
  }
  const pageCount = doc?.numPages ?? 0;
  return (
    <>
      {pageCount > 1 ? (
        <div className={styles.nav}>
          <Button disabled={pageIndex === 0 || rendering} onClick={() => setPageIndex((i) => Math.max(0, i - 1))}>Previous page</Button>
          <Caption1>Page {pageIndex + 1} of {pageCount}</Caption1>
          <Button disabled={pageIndex >= pageCount - 1 || rendering} onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}>Next page</Button>
        </div>
      ) : null}
      {rendering ? <Spinner size="tiny" label="Drawing the page" labelPosition="after" /> : null}
      <div className={styles.stage}>
        <canvas ref={canvas} className={styles.canvas} role="img" aria-label={`${label}, page ${pageIndex + 1}`} />
      </div>
    </>
  );
}
